/* eslint-disable no-else-return */
import PropTypes from "prop-types";
import styled from "styled-components";
import generatePath from "utils/generatePath";
import Text from "components/typography/Text";
import NavItemLink from "components/base/NavItemLink";
import Nav from "components/layout/Nav";
import Icon from "components/global/Icon";
import { useTranslation } from "react-i18next";
import generateQueryPath from "utils/generateQueryPath";

// ** SVG Static Imports
import { ReactComponent as LightAngleRight } from "resources/icons/light/angle-right.svg";
import { ReactComponent as LightAngleLeft } from "resources/icons/light/angle-left.svg";
import { ReactComponent as LightAngleDoubleRight } from "resources/icons/light/angle-double-right.svg";
import { ReactComponent as LightAngleDoubleLeft } from "resources/icons/light/angle-double-left.svg";

// #################################################################

const Navigation = styled(Nav.Panel)`
	a[class="active"] > div {
		font-weight: normal;
	}
`;

const StyledItem = styled(Nav.Item.Content)`
	min-width: 2.5rem;
	line-height: 1rem;
	padding: 0.65rem 0.75rem;
	color: ${({ theme: { colors } }) => colors.primary};
`;

// #################################################################

const PrevPageBtn = ({
	page,
	path,
	pathParams,
	onPage,
	pathname,
	query,
	noTo,
	...props
}) => {
	const ariaProps = {
		"aria-label": `Goto page ${page}`,
	};

	const to = path
		? generatePath(path, {
				page: page - 1,
				...pathParams,
		  })
		: pathname
		? generateQueryPath(pathname, {
				...query,
				page: page - 1,
		  })
		: "#";

	return (
		<NavItemLink
			noTo={noTo}
			to={to}
			container={Nav.Item}
			onClick={() => {
				if (onPage) {
					onPage(page - 1);
				}
			}}
			isActive={() => false}
			{...ariaProps}
			{...props}
		>
			<StyledItem>
				<Icon
					svgIconComponent={LightAngleLeft}
					icon="angle-left"
					type="fal"
				/>
			</StyledItem>
		</NavItemLink>
	);
};

const FirstPageBtn = ({
	path,
	pathParams,
	onPage,
	pathname,
	query,
	noTo,
	...props
}) => {
	const ariaProps = {
		"aria-label": "Goto page 1",
	};

	const to = path
		? generatePath(path, {
				page: 1,
				...pathParams,
		  })
		: pathname
		? generateQueryPath(pathname, {
				...query,
				page: 1,
		  })
		: "#";

	return (
		<NavItemLink
			noTo={noTo}
			to={to}
			container={Nav.Item}
			onClick={() => {
				if (onPage) {
					onPage(1);
				}
			}}
			isActive={() => false}
			{...ariaProps}
			{...props}
		>
			<StyledItem>
				<Icon
					svgIconComponent={LightAngleDoubleLeft}
					icon="angle-double-left"
					type="fal"
				/>
			</StyledItem>
		</NavItemLink>
	);
};
const LastPageBtn = ({
	total,
	path,
	pathParams,
	onPage,
	pathname,
	query,
	noTo,
	...props
}) => {
	const ariaProps = {
		"aria-label": `Goto page ${total}`,
	};

	const to = path
		? generatePath(path, {
				page: total,
				...pathParams,
		  })
		: pathname
		? generateQueryPath(pathname, {
				...query,
				page: total,
		  })
		: "#";

	return (
		<NavItemLink
			noTo={noTo}
			to={to}
			container={Nav.Item}
			onClick={() => {
				if (onPage) {
					onPage(total);
				}
			}}
			isActive={() => false}
			{...ariaProps}
			{...props}
		>
			<StyledItem>
				<Icon
					svgIconComponent={LightAngleDoubleRight}
					icon="angle-double-right"
					type="fal"
				/>
			</StyledItem>
		</NavItemLink>
	);
};

const NextPageBtn = ({
	page,
	path,
	pathParams,
	onPage,
	pathname,
	query,
	noTo,
	...props
}) => {
	const ariaProps = {
		"aria-label": `Goto page ${page}`,
	};

	const to = path
		? generatePath(path, {
				page: page + 1,
				...pathParams,
		  })
		: pathname
		? generateQueryPath(pathname, {
				...query,
				page: page + 1,
		  })
		: "#";

	return (
		<NavItemLink
			noTo={noTo}
			to={to}
			container={Nav.Item}
			onClick={() => {
				if (onPage) {
					onPage(page + 1);
				}
			}}
			isActive={() => false}
			{...ariaProps}
			{...props}
		>
			<StyledItem>
				<Icon
					svgIconComponent={LightAngleRight}
					icon="angle-right"
					type="fal"
				/>
			</StyledItem>
		</NavItemLink>
	);
};

// #################################################################

const Pagination = ({
	current,
	total,
	path,
	pathParams,
	query,
	pathname: pathnameFromProps,
	showWhenOnePage,
	linkScrollTo: scrollProps,
	onPage,
	noTo = false,
}) => {
	const linkScrollTo = Boolean(scrollProps);
	const { t } = useTranslation(["common"]);

	const pathname = pathnameFromProps
		? pathnameFromProps.replace(/\/$/, "")
		: "";

	if (!total || (Number(total) <= 1 && !showWhenOnePage)) {
		return null;
	}
	const pages = Array(total)
		.fill()
		.map((_, index) => index);

	const finalPages = () => {
		if (total <= 10) {
			return pages;
		} else if (current <= 5) {
			return pages.slice(0, 10);
		} else if (current > 5 && current <= total - 5) {
			return pages.slice(current - 5, current + 5);
		} else {
			return pages.slice(total - 10, total);
		}
	};

	return (
		<Navigation
			appearance="pills"
			role="navigation"
			aria-label="Pagination navigation"
			noMargin
		>
			<Text bold mr={2}>
				{t("pages")}:
			</Text>
			<Nav.List noPad>
				{current > 5 && total > 10 ? (
					<FirstPageBtn
						onPage={onPage}
						path={path}
						pathParams={pathParams}
						scroll={linkScrollTo}
						pathname={pathname}
						query={query}
						noTo={noTo}
					/>
				) : null}
				{current !== 1 ? (
					<PrevPageBtn
						onPage={onPage}
						page={current}
						path={path}
						pathParams={pathParams}
						scroll={linkScrollTo}
						pathname={pathname}
						query={query}
						noTo={noTo}
					/>
				) : null}
				{finalPages().map((index) => {
					const pageNumber = index + 1;
					const key = `page-${pageNumber}`;
					const active = pageNumber === Number(current);
					const to = path
						? generatePath(path, {
								page: pageNumber,
								...pathParams,
						  })
						: pathname
						? generateQueryPath(pathname, {
								...query,
								page: pageNumber,
						  })
						: "#";
					const ariaProps = {
						"aria-label": `Goto page ${pageNumber}`,
					};
					if (active) {
						ariaProps["aria-current"] = "true";
					}
					return (
						<NavItemLink
							onClick={() => {
								if (onPage) {
									onPage(pageNumber);
								}
							}}
							isActive={() => active}
							noTo={noTo}
							to={{
								pathname: to,
								state: {
									forceReload: active ? true : null,
								},
							}}
							container={Nav.Item}
							{...ariaProps}
							key={key}
							scroll={linkScrollTo}
						>
							<StyledItem>{pageNumber}</StyledItem>
						</NavItemLink>
					);
				})}
				{current !== total ? (
					<NextPageBtn
						onPage={onPage}
						page={current}
						path={path}
						pathParams={pathParams}
						scroll={linkScrollTo}
						pathname={pathname}
						query={query}
						noTo={noTo}
					/>
				) : null}
				{current < total - 5 && total > 10 ? (
					<LastPageBtn
						onPage={onPage}
						path={path}
						pathParams={pathParams}
						total={total}
						scroll={linkScrollTo}
						pathname={pathname}
						query={query}
						noTo={noTo}
					/>
				) : null}
			</Nav.List>
		</Navigation>
	);
};

// #################################################################

Pagination.defaultProps = {
	showWhenOnePage: false,
};

Pagination.propTypes = {
	current: PropTypes.number.isRequired,
	total: PropTypes.number,
	path: PropTypes.string,
	onPage: PropTypes.func,
	showWhenOnePage: PropTypes.bool,
};

// #################################################################

export default Pagination;
